import {
  ABCampaignGoalMetric,
  ABCampaignSegments,
  ABCampaignTargetDevice
} from '@/common/model/recommend/abCampaign'
import {
  RecommendationDistribution,
  RecommendationDistributionStatus
} from '@/common/model/recommend/recommendationDistribution'
import type { ConversionAttribution } from '@/common/model/recommend/conversion'

export const RecommendationDistributionApiPath = {
  LIST_DISTRIBUTIONS: '/api/sites/{site_id}/recommendation-distributions', // GET List
  GET_DISTRIBUTION: '/api/sites/{site_id}/recommendation-distributions/{distribution_id}', // GET
  CREATE_DISTRIBUTION: '/api/sites/{site_id}/recommendation-distributions', // POST
  UPDATE_DISTRIBUTION: '/api/sites/{site_id}/recommendation-distributions/{distribution_id}', // PUT
  COMPLETE_DISTRIBUTION:
    '/api/sites/{site_id}/recommendation-distributions/{distribution_id}/complete', // POST
  DISTRIBUTION_STATS: '/api/sites/{site_id}/recommendation-distributions/{distribution_id}/stats', // GET
  DISTRIBUTION_DAILY_SERIES:
    '/api/sites/{site_id}/recommendation-distributions/{distribution_id}/daily-series' // GET
}

export type RecommendationDistributionApiPath =
  (typeof RecommendationDistributionApiPath)[keyof typeof RecommendationDistributionApiPath]

export interface ListDistributionsVars {
  site_id: number
}

export interface GetDistributionVars {
  site_id: number
  distribution_id: number
}

// バリアント入力（既存パーツを参照）
export interface DistributionVariantInput {
  widget_id: number
  ab_traffic: number
}

export interface CreateDistributionVars {
  site_id: number
  name: string
  code: string
  status?: RecommendationDistributionStatus
  ab_enabled: boolean
  goal_metric?: ABCampaignGoalMetric
  cv_attribution?: ConversionAttribution
  target_devices?: ABCampaignTargetDevice[] | null
  target_segments?: ABCampaignSegments[] | null
  min_sample_per_variant?: number
  confidence_level?: number
  distributes_at?: string | null
  variants: DistributionVariantInput[]
}

export interface UpdateDistributionVars {
  site_id: number
  distribution_id: number
  name?: string | null
  code?: string | null
  status?: RecommendationDistributionStatus | null
  ab_enabled?: boolean | null
  goal_metric?: ABCampaignGoalMetric | null
  cv_attribution?: ConversionAttribution | null
  target_devices?: ABCampaignTargetDevice[] | null
  target_segments?: ABCampaignSegments[] | null
  min_sample_per_variant?: number | null
  confidence_level?: number | null
  distributes_at?: string | null
  variants?: DistributionVariantInput[] | null
}

export type ListDistributionsResponse = RecommendationDistribution[]
export type GetDistributionResponse = RecommendationDistribution
export type CreateDistributionResponse = RecommendationDistribution
export type UpdateDistributionResponse = RecommendationDistribution

// --- A/Bテスト結果（stats / daily-series / complete） ---
export interface DistributionStatsVars {
  site_id: number
  distribution_id: number
}

export interface DistributionDailySeriesVars {
  site_id: number
  distribution_id: number
}

export interface CompleteDistributionVars {
  site_id: number
  distribution_id: number
  winner_variant?: string | null
}

export interface DistributionVariantStat {
  group: string
  impressions: number
  clicks: number
  conversions: number
  revenue: number
  // 判定に使ったレート。配信の cv_attribution に従い、画面の表示切替では変わらない
  rate: number
  direct_conversions: number
  direct_revenue: number
  assist_conversions: number
  assist_revenue: number
}

export interface DistributionSignificance {
  is_significant: boolean
  p_value: number
  confidence_level: number
  winner: string | null
  lift_percent: number
  z_score: number
}

export interface DistributionStatsResponse {
  distribution_id: number
  name: string
  goal_metric: ABCampaignGoalMetric
  // 判定に使ったコンバージョン種別。画面は「判定指標」として常時表示する
  cv_attribution: ConversionAttribution
  status: RecommendationDistributionStatus
  variants: DistributionVariantStat[]
  sample_sufficient: boolean
  significance: DistributionSignificance | null
  recommended_sample_size: number
}

export interface DistributionDailyVariant {
  impressions: number
  cumulative_rate: number
}

export interface DistributionDailySeriesResponse {
  series: { date: string; variants: Record<string, DistributionDailyVariant> }[]
}

export type CompleteDistributionResponse = RecommendationDistribution
